/**
 * RECEPTION-OS-BUILD-V1 -- the Q-06=B "Recently viewed" patient strip
 * (founder letter 2026-08-03: recently-VIEWED only, per-tab/session; never
 * a browsable clinic-recent list).
 *
 * Renders up to PATIENT_RECENTLY_VIEWED_DISPLAY_CAP quiet links for patient
 * records THIS TAB'S operator already opened. Names are resolved at render
 * through the EXISTING fenced single-record read (fetchPatientMasterRecord)
 * -- one bounded read per shown id, never a list read (UAT-03). An id the
 * server refuses (stale scope, merged tombstone) simply does not render:
 * the memory is view-layer only and can never widen access.
 *
 * Empty memory renders NOTHING (not an empty-state card): the strip is an
 * accelerator under the search hero, not a workload surface -- S-4's "no
 * two regions disagree about work owed" stays with the day board.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { fetchPatientMasterRecord } from '@/dashboard/api';
import {
  PATIENT_RECENTLY_VIEWED_DISPLAY_CAP,
  readRecentlyViewedPatientIds,
} from '@/dashboard/patients/patient-recently-viewed';
import {
  deskRecordHref,
  type DeskRecordOrigin,
} from '@/dashboard/patients/patient-master-view';

export const RECENTLY_VIEWED_STRIP_LABEL = 'Recently viewed';

interface StripEntry {
  readonly id: string;
  readonly name: string;
}

export default function RecentlyViewedPatientsStrip({
  origin = null,
}: {
  /** L-18: the door the HOST is. A strip never assumes where it is mounted,
   *  and no origin leaves the href byte-identical to the pre-package one. */
  readonly origin?: DeskRecordOrigin | null;
} = {}): JSX.Element | null {
  const location = useLocation();
  const [entries, setEntries] = useState<readonly StripEntry[]>([]);

  useEffect(() => {
    const ids = readRecentlyViewedPatientIds().slice(
      0,
      PATIENT_RECENTLY_VIEWED_DISPLAY_CAP,
    );
    if (ids.length === 0) return;
    let cancelled = false;
    void Promise.all(
      ids.map((id) =>
        fetchPatientMasterRecord(id)
          .then((r) => ({ id, name: r.patient.fullName }))
          .catch(() => null),
      ),
    ).then((resolved) => {
      if (cancelled) return;
      setEntries(resolved.filter((e): e is StripEntry => e !== null));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (entries.length === 0) return null;
  return (
    <nav
      className="today-home__recent"
      aria-label={RECENTLY_VIEWED_STRIP_LABEL}
      data-testid="home-recently-viewed"
    >
      <span className="today-home__recent-label">
        {RECENTLY_VIEWED_STRIP_LABEL}
      </span>
      {entries.map((e) => (
        <Link
          key={e.id}
          to={deskRecordHref(e.id, origin, location.search)}
          className="today-home__recent-link"
          data-testid={`home-recent-patient-${e.id}`}
        >
          {e.name}
        </Link>
      ))}
    </nav>
  );
}
